import { useUserMe } from 'api/goodtrust/user'
import { emailUserMemory, publicizeUserMemory } from 'api/goodtrust/user/memory'
import { createFutureMessageWithRedirect } from 'components/futureMessage/FutureMessageUtils'
import { Image } from 'components/Image'
import { SelectedMemory } from 'components/memory/MemoryUtils'
import { EmailModalFields, useEmailModal } from 'components/modal/emailModal/EmailModal'
import { Spacer } from 'components/spacer/Spacer'
import { useStoryPortraitLogic } from 'components/storyPortraits/logic'
import { handleAndToastError, toastSuccess } from 'components/Toast'
import { Caption } from 'components/Typography'
import { AutoPlayer } from 'components/videoPlayer/autoPlayer/AutoPlayer'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'
import { mutate } from 'swr'
import { fireEvent } from 'utils/analytics'
import { Platform } from 'utils/analytics/types'
import { encodeQuery, unwrapResponse } from 'utils/fetcher'
import { useAuthAwareFetcher } from 'utils/hooks'
import { ShareItems } from './ShareItems'

export const MemoryShareItems = (props: {
  memory?: SelectedMemory
  className?: string
  onFutureMessage?: () => void
  isModal?: boolean
  caption?: string
  round?: boolean
}) => {
  const { t } = useTranslation('life_stories')
  const { t: tMemory } = useTranslation('memory')
  const user = useUserMe()
  const fetcher = useAuthAwareFetcher()
  const storyLogic = useStoryPortraitLogic()

  const onShare = (platform: Platform) =>
    new Promise<boolean>(async (resolve, reject) => {
      try {
        if (!props.memory?.publicized) {
          await publicizeUserMemory(
            props.memory?.uuid as string,
            props.isModal ? 'INSIDE_FLOW' : 'OUTSIDE_FLOW'
          ).then(unwrapResponse)
          await mutate(`/api/v1/memory/${props.memory?.uuid}`)
        }
        if (props.memory?.selectedAnimation?.animation_type === 'STORY_PORTRAIT') {
          storyLogic.onShared(platform)
        } else {
          fireEvent(
            props.memory?.selectedAnimation?.animation_type === 'LIVE_PORTRAIT'
              ? 'animation_shared'
              : 'sh_animation_shared',
            {
              platform,
              driver: props.memory?.selectedAnimation?.driver as string,
            }
          )
        }
        resolve(true)
      } catch (err) {
        reject(err || { message: 'Failed to share memory' })
      }
    })

  const onMail = async (payload: EmailModalFields) => {
    const res = await emailUserMemory(props.memory?.uuid as string, {
      email: payload.recipient_email,
      name: payload.name,
      sender_email: payload.sender_email,
      animation_uuid: props.memory?.selectedAnimation?.uuid,
    })
    if (!res.ok) {
      handleAndToastError(res.error ?? 'failed_to_share_via_email')
    } else {
      toastSuccess('shared_via_email')
    }
  }

  const [emailModal, openEmailModal] = useEmailModal({
    title: tMemory('memory.memory_id.share_items_email_modal_title'),
    text: <>{tMemory('memory.memory_id.share_items_email_modal_text')}</>,
    preview: props.memory?.selectedAnimation?.file?.url ? (
      <AutoPlayer src={props.memory.selectedAnimation.file?.url} />
    ) : (
      <Image src={props.memory?.original_file?.url} />
    ),
    onSend: async (payload) => {
      await onShare('email').catch((err) => {
        handleAndToastError(err)
        return
      })
      await onMail(payload)
    },
  })

  const shareUrl = useMemo(() => {
    return encodeQuery(
      `${process.env.NEXT_PUBLIC_WEBSITE_URL}/${
        props.memory?.selectedAnimation?.animation_type === 'STORY_PORTRAIT'
          ? 'story-portraits'
          : props.memory?.selectedAnimation?.animation_type === 'SINGING_PORTRAIT'
          ? 'singing-portraits'
          : 'memories'
      }/shared/${props.memory?.uuid}`,
      { animationUuid: props.memory?.selectedAnimation?.uuid }
    )
  }, [props.memory])

  return (
    <>
      {props.caption && (
        <>
          <Caption>{props.caption}</Caption>
          <Spacer size={[1.5, 1]} />
        </>
      )}
      <ShareItems
        className={props.className}
        isModal={props.isModal}
        url={shareUrl}
        text={t('life_stories.inside_page.share_item_text', {
          who: user.data?.json?.first_name || user.data?.json?.email,
        })}
        hashtag="GoodTrustMemories"
        via="MyGoodTrust"
        emailModal={emailModal}
        openEmailModal={openEmailModal}
        onShare={onShare}
        round={props.round}
        onFutureMessage={async () => {
          props.onFutureMessage?.()
          await createFutureMessageWithRedirect({
            fetcher: fetcher,
            type: 'share',
            redirectParams: {
              memoryType: props.memory?.selectedAnimation ? 'video' : 'image',
              memoryFileUrl:
                props.memory?.selectedAnimation?.file?.url ?? props.memory?.original_file?.url,
            },
          })
        }}
      />
    </>
  )
}
